/**
 * Studio Mode Indicator
 *
 * Shows the current studio mode (local or cloud) with an icon.
 * Shows "Read Only" badge for lapsed subscriptions.
 */

import { Cloud, HardDrive, Lock } from 'lucide-react'
import { useStudio } from '@/context/StudioContext'

interface StudioModeIndicatorProps {
  /** Whether to show the text label */
  showLabel?: boolean
  /** Size variant */
  size?: 'sm' | 'md'
}

export function StudioModeIndicator({ showLabel = true, size = 'md' }: StudioModeIndicatorProps) {
  const { mode, readOnly } = useStudio()

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm'

  if (mode === 'cloud') {
    if (readOnly) {
      return (
        <div
          className="flex items-center gap-2"
          title="Cloud Studio (Read Only) - Renew subscription to edit"
        >
          <div className="relative">
            <Cloud className={`${iconSize} text-[var(--color-kids-warning)]`} />
            <Lock className="w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 text-[var(--color-kids-warning)]" />
          </div>
          {showLabel && (
            <span className={`${textSize} text-[var(--color-kids-warning)] font-medium`}>
              Read Only
            </span>
          )}
        </div>
      )
    }

    return (
      <div
        className="flex items-center gap-2"
        title="Cloud Studio - Synced across devices"
      >
        <Cloud className={`${iconSize} text-[var(--color-kids-success)]`} />
        {showLabel && (
          <span className={`${textSize} text-[var(--color-kids-success)] font-medium`}>
            Cloud Studio
          </span>
        )}
      </div>
    )
  }

  // Local mode
  return (
    <div
      className="flex items-center gap-2"
      title="Local Studio - Saved on this device only"
    >
      <HardDrive className={`${iconSize} text-[var(--color-kids-text-muted)]`} />
      {showLabel && (
        <span className={`${textSize} text-[var(--color-kids-text-muted)] font-medium`}>
          Local Studio
        </span>
      )}
    </div>
  )
}
